import * as React from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAppDispatch } from '@user/store/hooks'
import { useSelector } from 'react-redux'
import {
  GridColDef,
  GridPaginationModel,
  GridSortModel,
} from '@mui/x-data-grid-pro'
import {
  FieldState,
  getOrderByFunc,
  orderByToSortModel,
} from '@user/components/xGrid'
import {
  getDefaultGuestUserLeadsColumnVisibility,
  selectGuestUserLeadsColumnOrder,
  selectGuestUserLeadsFilters,
  setLeadsPage,
  setLeadsPageSize,
  useSaveGuestUserLeadsFilters,
} from '@user/store/guestUser/slice'
import { getSaveListViewPreferenceFunc } from '@user/store/userPreferences'
import {
  getGuestUserLeadListExport,
  getGuestUserLeads,
} from '@user/store/guestUser/api'
import { setGuestUserDashboardLeadsColumnOrder } from '@user/store/userPreferences/slice'
import { debounce } from 'lodash'
import { updateLoadingState } from '@sales-center/common'
import { toast } from '@sales-center/components'
import { leadSortConfig } from '@user/components/Leads/LeadList/leadListColumns'

const getOrderBy = getOrderByFunc('createdDate desc', leadSortConfig)
const saveListViewPreference = getSaveListViewPreferenceFunc(
  'guestUserDashboardLeads',
)

export const useGuestUserLeadsActions = (props: any) => {
  const dispatch = useAppDispatch()
  const { filterSet } = useSelector(selectGuestUserLeadsFilters)
  const saveFilters = useSaveGuestUserLeadsFilters()
  const [searchText, setSearchText] = useState<string>(filterSet.search)
  const columnOrder = useSelector(selectGuestUserLeadsColumnOrder)
  const defaultVisibility = getDefaultGuestUserLeadsColumnVisibility()

  useEffect(() => {
    setSearchText(filterSet.search)
  }, [filterSet.search])

  const debouncedDispatch = useMemo(
    () =>
      debounce((val: string) => {
        saveFilters({ ...filterSet, search: val })
      }, 500),
    [filterSet, saveFilters],
  )

  const handleSearchChange = (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    setSearchText(e.target.value)
    debouncedDispatch(e.target.value)
  }

  const handlePaginationModelChange = useCallback(
    (pageModel: GridPaginationModel) => {
      const { page, pageSize } = pageModel
      dispatch(setLeadsPage(page + 1))
      dispatch(setLeadsPageSize(pageSize))
      // Trigger API call with new pagination
      dispatch(getGuestUserLeads())
    },
    [dispatch],
  )

  const sortModel = useMemo(
    () =>
      props.columns.length === 0
        ? undefined
        : orderByToSortModel(filterSet.orderBy, leadSortConfig),
    [filterSet.orderBy, props.columns.length],
  )

  const onSortModelChange = useCallback(
    (sortModel: GridSortModel) => {
      const newOrderBy = getOrderBy(sortModel)
      if (newOrderBy !== filterSet.orderBy) {
        saveFilters({ ...filterSet, orderBy: newOrderBy })
      }
    },
    [filterSet, saveFilters],
  )

  const onSaveColumnState = useCallback(
    (columnOrder: FieldState[], columns: GridColDef[]) => {
      const mappedData = columnOrder?.map((row: FieldState) => {
        const found = columns?.find(
          (findRow: GridColDef) => findRow?.field == row?.field,
        )
        if (found && found?.width) {
          return {
            ...row,
            width: found?.width?.toString(),
          }
        }
        return {
          ...row,
        }
      })
      // Save to both local state and backend
      dispatch(setGuestUserDashboardLeadsColumnOrder(mappedData))
      saveListViewPreference(dispatch, mappedData)
    },
    [dispatch],
  )

  const selectionChangeCallback = useCallback(
    (selectionModel: any) => {
      props.setFiltersDisabled(selectionModel.length > 0)
    },
    [props],
  )

  const onColumnWidthChange = useCallback(
    (
      colDef: GridColDef | undefined,
      columns: GridColDef[],
      columnVisibility: any,
    ) => {
      if (colDef) {
        const updatedColumns = columns.map((col) =>
          col.field === colDef.field ? { ...col, width: colDef.width } : col,
        )
        const columnOrder = updatedColumns.map((col) => ({
          field: col.field,
          width: col.width?.toString() || '',
          hide: columnVisibility[col.field] === false,
        }))
        // Save to both local state and backend
        dispatch(setGuestUserDashboardLeadsColumnOrder(columnOrder))
        saveListViewPreference(dispatch, columnOrder)
      }
    },
    [dispatch],
  )

  const onClickExport = React.useCallback(async () => {
    let columns: string[] = []

    if (columnOrder.length > 0) {
      columns = columnOrder.filter((x) => x.hide === false).map((x) => x.field)
    } else {
      columns = Object.keys(defaultVisibility).filter(
        (column) => defaultVisibility[column],
      )
    }

    dispatch(updateLoadingState('visible'))
    const data = await dispatch(
      getGuestUserLeadListExport({
        columns: columns,
        multiServiceLocations: true,
      }),
    )

    if (getGuestUserLeadListExport.fulfilled.match(data)) {
      dispatch(updateLoadingState('hidden'))
      return data.payload
    } else {
      dispatch(updateLoadingState('hidden'))
      toast.error('An Error occurred.  Unable to Export List.')
    }
  }, [columnOrder, defaultVisibility, dispatch])

  return {
    handlePaginationModelChange,
    sortModel,
    onSortModelChange,
    onSaveColumnState,
    selectionChangeCallback,
    onColumnWidthChange,
    handleSearchChange,
    searchText,
    onClickExport,
  }
}
